const ANCHOR_ATTRIBUTES = ["data-testid", "data-sentry-component"] as const
const STRUCTURAL_ROLES: ReadonlySet<string> = new Set([
  "alertdialog",
  "complementary",
  "dialog",
  "feed",
  "grid",
  "listbox",
  "log",
  "main",
  "menu",
  "navigation",
  "region",
  "tablist",
  "tree",
])
const VOLATILE_VALUE_PATTERN = /[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}|\d+/gi
const VOLATILE_VALUE_PLACEHOLDER = ":id"
const SOURCE_FILE_ATTRIBUTE = "data-sentry-source-file"
const SELECTOR_STEP_SEPARATOR = " > "
const UNANCHORED_ROOT_SELECTOR = "body"

export interface ScrollBoxIdentity {
  key: string
  sourceFile: string | null
}

export function stableIdentifierText(value: string): string {
  return value.replace(VOLATILE_VALUE_PATTERN, VOLATILE_VALUE_PLACEHOLDER)
}

function anchorSelector(element: Element): string | null {
  for (const attribute of ANCHOR_ATTRIBUTES) {
    const value = element.getAttribute(attribute)
    if (value) return `[${attribute}="${stableIdentifierText(value)}"]`
  }
  const role = element.getAttribute("role")
  return role && STRUCTURAL_ROLES.has(role) ? `[role="${role}"]` : null
}

function stepSelector(element: Element, parent: Element): string {
  const sameTagSiblings = Array.from(parent.children).filter((sibling) => sibling.tagName === element.tagName)
  const tag = element.tagName.toLowerCase()
  return sameTagSiblings.length === 1 ? tag : `${tag}:nth-of-type(${sameTagSiblings.indexOf(element) + 1})`
}

function selectorFromNearestAnchor(box: Element): string {
  const steps: string[] = []
  let element = box
  while (element !== document.body && element.parentElement) {
    const anchor = anchorSelector(element)
    if (anchor) return [anchor, ...steps].join(SELECTOR_STEP_SEPARATOR)
    steps.unshift(stepSelector(element, element.parentElement))
    element = element.parentElement
  }
  return [UNANCHORED_ROOT_SELECTOR, ...steps].join(SELECTOR_STEP_SEPARATOR)
}

export function identifyScrollBox(box: Element): ScrollBoxIdentity {
  return {
    key: selectorFromNearestAnchor(box),
    sourceFile: box.closest(`[${SOURCE_FILE_ATTRIBUTE}]`)?.getAttribute(SOURCE_FILE_ATTRIBUTE) ?? null,
  }
}
